'use client';

import {
  IFormTemplateKey,
  useFormTemplateKey,
  useFormTemplateKeys,
} from '@commenda/api/form-builder/form-template-keys';
import {
  Button,
  Input,
  LoadingIndicator,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@commenda/kakashi';
import { ChevronDown, Search } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

const KEY_SELECTOR_PAGE_SIZE = 20;
const DEBOUNCE_MS = 300;

export function KeySelector({
  value,
  onValueChange,
  placeholder = 'Select a template key',
}: {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [allKeys, setAllKeys] = useState<IFormTemplateKey[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Fetch the selected key separately to always have its data for display
  const { key: selectedKeyData } = useFormTemplateKey(value || null);

  // Fetch keys with pagination
  const { keys, isLoading, count, mutate } = useFormTemplateKeys({
    hasLiveForm: true,
    searchTerm: debouncedSearch || undefined,
    limit: KEY_SELECTOR_PAGE_SIZE,
    offset,
  });

  // Debounce search input - only clear allKeys when search term actually changes
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setOffset(0);
      // Only clear keys when actively searching
      if (searchTerm) {
        setAllKeys([]);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm]);

  // Accumulate keys for load more
  useEffect(() => {
    if (keys && keys.length > 0) {
      setAllKeys((prev) => {
        if (offset === 0) {
          return keys;
        }
        // Merge new keys, avoiding duplicates
        const existingIds = new Set(prev.map((k) => k.id));
        const newKeys = keys.filter((k) => !existingIds.has(k.id));
        return [...prev, ...newKeys];
      });
    } else if (offset === 0 && keys && debouncedSearch) {
      // Only clear if we're searching and got no results
      setAllKeys([]);
    }
  }, [keys, offset, debouncedSearch]);

  // Refetch when dropdown opens, reset search when it closes
  useEffect(() => {
    if (isOpen) {
      setOffset(0);
      mutate();
      setTimeout(() => searchInputRef.current?.focus(), 0);
    } else {
      setSearchTerm('');
      setDebouncedSearch('');
    }
  }, [isOpen, mutate]);

  const hasMore = allKeys.length < count;

  const handleLoadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      setOffset((prev) => prev + KEY_SELECTOR_PAGE_SIZE);
    }
  }, [isLoading, hasMore]);

  const displayValue = selectedKeyData?.name || '';

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-10 w-full box-border items-center justify-between rounded-md px-xl py-2.5 bg-bg-primary txt-small-regular ring-0 transition-colors border border-border-primary hover:border-border-primary-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-border-primary-hover focus-visible:border-border-primary-hover aria-expanded:ring-2 aria-expanded:ring-fg-brand aria-expanded:border-transparent"
        >
          <span
            className={
              displayValue ? 'text-text-primary' : 'text-text-placeholder'
            }
          >
            {displayValue || placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 ml-2 text-fg-quaternary" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        align="start"
        style={{ width: 'var(--radix-popper-anchor-width)' }}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Search input */}
        <div className="border-b border-border-secondary p-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
            <Input
              ref={searchInputRef}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search keys..."
              className="pl-8 h-8 txt-small-regular"
            />
          </div>
        </div>

        {/* Scrollable content */}
        <div className="max-h-[250px] overflow-y-auto">
          {allKeys.length === 0 && !isLoading ? (
            <div className="px-2 py-4 text-center">
              <p className="txt-small-regular text-text-tertiary">
                {searchTerm
                  ? 'No keys found matching your search'
                  : 'No template keys with live forms available'}
              </p>
            </div>
          ) : (
            <>
              {/* Keys list */}
              {allKeys.map((key) => (
                <button
                  key={key.id}
                  type="button"
                  className={`relative flex w-full cursor-pointer select-none items-center py-2 px-3 txt-small-regular outline-none hover:bg-bg-active-light text-left ${
                    value === key.id
                      ? 'bg-bg-active-light text-text-primary txt-small-medium'
                      : 'text-text-tertiary'
                  }`}
                  onClick={() => {
                    onValueChange(key.id);
                    setIsOpen(false);
                  }}
                >
                  <div className="flex flex-col gap-0.5 flex-1">
                    <span className="txt-small-medium">{key.name}</span>
                    {key.description && (
                      <span className="txt-xs-regular text-text-tertiary truncate max-w-[280px]">
                        {key.description}
                      </span>
                    )}
                  </div>
                </button>
              ))}

              {/* Loading indicator */}
              {isLoading && (
                <div className="flex items-center justify-center py-3">
                  <LoadingIndicator size={16} />
                </div>
              )}

              {/* Load more button */}
              {!isLoading && hasMore && (
                <div className="px-2 py-2 border-t border-border-secondary">
                  <Button
                    variant="tertiary"
                    size="sm"
                    className="w-full"
                    onClick={handleLoadMore}
                  >
                    Load more ({count - allKeys.length} remaining)
                  </Button>
                </div>
              )}

              {/* End of list indicator */}
              {!isLoading && allKeys.length > 0 && !hasMore && (
                <div className="px-2 py-2 text-center">
                  <p className="txt-xs-regular text-text-tertiary">
                    {allKeys.length} key{allKeys.length !== 1 ? 's' : ''} loaded
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
